import { BaseComponent } from '../lib/BaseComponent.js';
import { api, Extinguisher } from '../lib/api.js';
import { formatDate } from '../lib/dates.js';
import { router } from '../lib/router.js';
import { buildAppNav, APP_NAV_STYLES } from '../lib/app-nav.js';
import { Toast } from './shared/Toast.js';

/**
 * Dashboard Page Component
 * Main view after authentication showing extinguishers overview
 */
export class DashboardPage extends BaseComponent {
  private isLoading = true;
  private extinguishers: Extinguisher[] = [];
  private errorMessage = '';

  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'dashboard-container' });

    // Header with navigation
    const header = this.createElement('header', { className: 'dashboard-header' });
    const headerContent = this.createElement('div', { className: 'header-content' });

    const logo = this.createElement('h1', { className: 'logo' }, 'FireGuard');
    headerContent.appendChild(logo);

    const nav = buildAppNav((t, p, txt) => this.createElement(t as any, p as any, txt as any), '/dashboard', () => this.handleLogout());
    headerContent.appendChild(nav);

    header.appendChild(headerContent);
    container.appendChild(header);

    // Main content
    const main = this.createElement('main', { className: 'dashboard-main' });

    // Trial / plan banner (loaded once, non-blocking)
    const banner = this.createElement('div', { className: 'plan-banner' });
    banner.style.display = 'none';
    main.appendChild(banner);
    this.loadPlanBanner(banner);

    // Anything the nightly compliance sweep flagged, loaded without blocking the page
    const attention = this.createElement('section', { className: 'attention' });
    attention.style.display = 'none';
    main.appendChild(attention);
    this.loadAlerts(attention);

    // Page title and actions
    const titleBar = this.createElement('div', { className: 'title-bar' });
    const titleSection = this.createElement('div', { className: 'title-section' });
    const title = this.createElement('h2', {}, 'Fire Extinguishers');
    const viewAllLink = this.createElement('a', { href: '/extinguishers', className: 'view-all-link' }, 'View All →');
    titleSection.appendChild(title);
    titleSection.appendChild(viewAllLink);
    const addBtn = this.createElement('button', { className: 'btn-primary' }, '+ Add Extinguisher');
    addBtn.addEventListener('click', () => this.openAddExtinguisherFlow());
    titleBar.appendChild(titleSection);
    titleBar.appendChild(addBtn);
    main.appendChild(titleBar);

    // Loading state
    if (this.isLoading) {
      const loading = this.createElement('div', { className: 'loading' }, 'Loading extinguishers...');
      main.appendChild(loading);
      container.appendChild(main);
      this.shadow.appendChild(container);

      // Fetch data
      await this.loadExtinguishers();
      return;
    }

    // Error state
    if (this.errorMessage) {
      const error = this.createElement('div', { className: 'error-message' }, this.errorMessage);
      main.appendChild(error);
      container.appendChild(main);
      this.shadow.appendChild(container);
      return;
    }

    // First-run onboarding: shown until the first inspection is recorded
    if (this.extinguishers.length === 0 || !this.extinguishers.some((e) => e.last_monthly_inspection)) {
      main.appendChild(this.createOnboarding());
      if (this.extinguishers.length === 0) {
        container.appendChild(main);
        this.shadow.appendChild(container);
        return;
      }
    }

    // Triage strip: what is overdue, split by who can actually clear it.
    const triage = this.createTriageStrip();
    if (triage) main.appendChild(triage);

    // Extinguishers grid
    const grid = this.createElement('div', { className: 'extinguishers-grid' });

    this.extinguishers.forEach((ext) => {
      const card = this.createExtinguisherCard(ext);
      grid.appendChild(card);
    });

    main.appendChild(grid);
    container.appendChild(main);
    this.shadow.appendChild(container);
  }

  /**
   * Four-step first-run checklist. Steps derive from data already loaded so the
   * dashboard never blocks on an extra request; the location count is fetched lazily.
   */
  /**
   * "Needs attention" panel. The nightly sweep records an alert whenever a unit
   * slips past a deadline; without this the alerts existed only in the database.
   */
  /**
   * A single "42 overdue" number reads like one problem with one price tag.
   * It is usually two: monthly checks your own staff can walk off today at no
   * cost, and annual service that needs a licensed contractor booked and paid.
   * Splitting them is the difference between paralysis and an afternoon of work.
   */
  private createTriageStrip(): HTMLElement | null {
    const staff = this.extinguishers.filter((e) => (e as any).compliance_actor === 'staff').length;
    const contractor = this.extinguishers.filter((e) => (e as any).compliance_actor === 'contractor').length;
    if (staff === 0 && contractor === 0) return null;

    const strip = this.createElement('section', { className: 'triage' });
    const items: Array<{ count: number; label: string; hint: string; actor: string; cls: string }> = [];
    if (staff > 0) {
      items.push({
        count: staff,
        label: staff === 1 ? 'unit your staff can clear' : 'units your staff can clear',
        hint: 'Monthly visual checks. No contractor, no cost.',
        actor: 'staff',
        cls: 'triage-staff',
      });
    }
    if (contractor > 0) {
      items.push({
        count: contractor,
        label: contractor === 1 ? 'unit needs a contractor' : 'units need a contractor',
        hint: 'Annual maintenance by a licensed technician.',
        actor: 'contractor',
        cls: 'triage-contractor',
      });
    }

    items.forEach(({ count, label, hint, actor, cls }) => {
      const card = this.createElement('button', { className: `triage-card ${cls}`, type: 'button' });
      card.appendChild(this.createElement('span', { className: 'triage-count' }, String(count)));
      card.appendChild(this.createElement('span', { className: 'triage-label' }, label));
      card.appendChild(this.createElement('span', { className: 'triage-hint' }, hint));
      card.addEventListener('click', () => router.navigate(`/extinguishers?actor=${actor}`));
      strip.appendChild(card);
    });

    return strip;
  }

  private async loadAlerts(host: HTMLElement): Promise<void> {
    try {
      const res = await api.get<{ alerts: Array<{ id: string; extinguisher_id: string; severity: string; message: string; created_at: number }> }>('/api/alerts?unread=true&limit=5');
      const alerts = res?.alerts ?? [];
      if (alerts.length === 0) return;

      host.replaceChildren();
      const header = this.createElement('div', { className: 'attention-header' });
      header.appendChild(this.createElement('h3', {}, `Needs attention (${alerts.length})`));
      const link = this.createElement('a', { href: '/extinguishers?compliance_status=overdue', className: 'attention-link' }, 'See all overdue →');
      link.addEventListener('click', (e) => {
        e.preventDefault();
        router.navigate('/extinguishers?compliance_status=overdue');
      });
      header.appendChild(link);
      host.appendChild(header);

      for (const alert of alerts) {
        const row = this.createElement('div', { className: `attention-row sev-${alert.severity}` });
        const msg = this.createElement('button', { className: 'attention-msg', type: 'button' }, alert.message);
        msg.addEventListener('click', () => router.navigate(`/extinguishers/${alert.extinguisher_id}`));
        const dismiss = this.createElement('button', { className: 'attention-dismiss', type: 'button', title: 'Dismiss' }, 'Dismiss');
        dismiss.addEventListener('click', async () => {
          dismiss.setAttribute('disabled', 'true');
          try {
            await api.post(`/api/alerts/dismiss/${alert.id}`, {});
            row.remove();
            if (!host.querySelector('.attention-row')) host.style.display = 'none';
          } catch {
            dismiss.removeAttribute('disabled');
          }
        });
        row.appendChild(msg);
        row.appendChild(dismiss);
        host.appendChild(row);
      }
      host.style.display = 'block';
    } catch {
      // Alerts are supplementary; never let them break the dashboard
    }
  }

  private createOnboarding(): HTMLElement {
    const card = this.createElement('section', { className: 'onboarding' });
    card.appendChild(this.createElement('div', { className: 'onboarding-eyebrow' }, 'Getting started'));
    card.appendChild(this.createElement('h3', {}, 'Get your extinguishers on the board, in four steps'));
    card.appendChild(this.createElement('p', { className: 'onboarding-sub' }, 'Creating a site takes a couple of minutes. Entering your units takes longer, and you can stop and come back. From then on FireGuard tracks the 31-day clock for each one and tells you what is due.'));

    const hasExt = this.extinguishers.length > 0;
    const hasInspection = this.extinguishers.some((e) => e.last_monthly_inspection);
    const steps: Array<{ title: string; desc: string; href: string; done: boolean; id: string }> = [
      { id: 'location', title: 'Add a location', desc: 'A building or site. Extinguishers belong to a location.', href: '/locations', done: hasExt },
      { id: 'extinguisher', title: 'Add your extinguishers', desc: 'Type, size, and where each one hangs. Have a list already? Extinguishers → Import spreadsheet takes a contractor export as-is.', href: '/extinguishers', done: hasExt },
      { id: 'labels', title: 'Print QR labels', desc: 'Open a location and choose Print QR labels. Stick one beside each inspection tag.', href: '/locations', done: hasInspection },
      { id: 'inspect', title: 'Run the first monthly check', desc: 'Scan a label on your phone and tap through the NFPA 10 checklist.', href: '/scan', done: hasInspection },
    ];
    const list = this.createElement('ol', { className: 'onboarding-steps' });
    steps.forEach((st, i) => {
      const li = this.createElement('li', { className: `onboarding-step${st.done ? ' done' : ''}` });
      li.appendChild(this.createElement('span', { className: 'onboarding-num' }, st.done ? '✓' : String(i + 1)));
      const body = this.createElement('div');
      body.appendChild(this.createElement('strong', {}, st.title));
      body.appendChild(this.createElement('p', {}, st.desc));
      li.appendChild(body);
      if (!st.done) {
        const go = this.createElement('a', { href: st.href, className: 'onboarding-go' }, i === 0 ? 'Start →' : 'Go →');
        li.appendChild(go);
      }
      list.appendChild(li);
    });
    card.appendChild(list);

    // Resolve step 1 precisely once locations are known.
    api.getLocations().then((res) => {
      const has = (res.locations || []).length > 0;
      const first = list.querySelector('.onboarding-step');
      if (first && has) {
        first.classList.add('done');
        const num = first.querySelector('.onboarding-num');
        if (num) num.textContent = '✓';
        first.querySelector('.onboarding-go')?.remove();
      }
    }).catch(() => undefined);

    return card;
  }

  private async loadPlanBanner(banner: HTMLElement): Promise<void> {
    try {
      const info = await api.getBilling();
      const ent = info.entitlements;
      let text = '';
      let cls = 'plan-banner-info';
      if (ent.status === 'expired') {
        text = 'Your 45-day trial has ended. Inspections and exports still work; choose a plan to add extinguishers.';
        cls = 'plan-banner-warn';
      } else if (ent.isTrial && ent.trialDaysLeft !== null) {
        text = `Trial: ${ent.trialDaysLeft} day${ent.trialDaysLeft === 1 ? '' : 's'} left. ${ent.activeExtinguishers} of ${ent.maxExtinguishers.toLocaleString()} extinguishers used.`;
        if (ent.trialDaysLeft <= 7) cls = 'plan-banner-warn';
      } else if (ent.blockReason) {
        text = ent.blockReason;
        cls = 'plan-banner-warn';
      } else if (ent.overLimit) {
        text = `You are over your plan limit (${ent.activeExtinguishers} of ${ent.maxExtinguishers}). Upgrade before ${ent.graceEndsAt ? new Date(ent.graceEndsAt).toLocaleDateString() : 'the grace period ends'}.`;
        cls = 'plan-banner-warn';
      }
      if (!text) return;
      banner.className = `plan-banner ${cls}`;
      banner.replaceChildren();
      banner.appendChild(this.createElement('span', {}, text));
      // Only an admin can actually change the plan: /api/billing/checkout and
      // the portal are both requireRole('admin'). Offering the link to a
      // manager or inspector sent them to a page that would refuse them, which
      // is the thing the role-gated nav exists to avoid. They still see the
      // status — it affects their work — but are told who can act on it.
      const role = (api.currentUser()?.role || '').toLowerCase();
      if (role === 'admin') {
        banner.appendChild(
          this.createElement('a', { href: '/billing' }, ent.isTrial || ent.status === 'expired' ? 'Choose a plan →' : 'Manage plan →')
        );
      } else {
        banner.appendChild(this.createElement('span', { className: 'plan-banner-note' }, 'An administrator can change the plan.'));
      }
      banner.style.display = 'flex';
    } catch {
      // Billing info is optional on the dashboard.
    }
  }

  private createExtinguisherCard(ext: Extinguisher): HTMLElement {
    const card = this.createElement('div', { className: 'extinguisher-card' });

    // Status badge
    const statusBadge = this.createElement('div', {
      className: `status-badge status-${ext.compliance_status}`,
    }, ext.compliance_status.toUpperCase());
    card.appendChild(statusBadge);

    // QR Code
    const qr = this.createElement('div', { className: 'qr-code' }, ext.qr_code);
    card.appendChild(qr);

    // Type and size
    const type = this.createElement('div', { className: 'extinguisher-type' }, `${ext.extinguisher_type} - ${ext.size_lbs} lbs`);
    card.appendChild(type);

    // Location
    const location = this.createElement('div', { className: 'location' }, `📍 ${ext.room_area}`);
    card.appendChild(location);

    // Last inspection
    const lastInspection = this.createElement('div', { className: 'last-inspection' });
    if (ext.last_monthly_inspection) {
      lastInspection.textContent = `Last inspection: ${formatDate(ext.last_monthly_inspection)}`;
    } else {
      lastInspection.textContent = 'Never inspected';
    }
    card.appendChild(lastInspection);

    // Actions
    const actions = this.createElement('div', { className: 'card-actions' });
    const inspectBtn = this.createElement('button', { className: 'btn-primary btn-small' }, 'Inspect');
    inspectBtn.addEventListener('click', () => {
      alert(`Inspect extinguisher ${ext.qr_code} - Coming soon!`);
    });
    actions.appendChild(inspectBtn);
    card.appendChild(actions);

    return card;
  }

  private async loadExtinguishers(): Promise<void> {
    try {
      const response = await api.getExtinguishers();
      this.extinguishers = response.extinguishers;
      this.isLoading = false;
      this.shadow.innerHTML = '';
      await this.render();
    } catch (error) {
      this.isLoading = false;
      this.errorMessage = (error as Error).message || 'Failed to load extinguishers';
      this.shadow.innerHTML = '';
      await this.render();
    }
  }

  private handleLogout(): void {
    api.logout();
    router.navigate('/login');
  }

  private getStyles(): string {
    return `
      ${APP_NAV_STYLES}

      .onboarding { background: #fff; border: 1px solid var(--gray-200); border-top: 4px solid var(--danger); border-radius: var(--border-radius); padding: 22px 24px; margin-bottom: 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
      .onboarding-eyebrow { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--danger); font-weight: 700; margin-bottom: 6px; }
      .onboarding h3 { font-size: 20px; margin-bottom: 6px; }
      .onboarding-sub { color: var(--gray-500); font-size: 14px; margin-bottom: 16px; }
      .onboarding-steps { list-style: none; display: grid; gap: 10px; }
      .onboarding-step { display: grid; grid-template-columns: 36px 1fr auto; gap: 14px; align-items: center; padding: 12px 14px; border: 1px solid var(--gray-200); border-radius: var(--border-radius); background: var(--gray-50); }
      .onboarding-step.done { opacity: 0.7; background: #ecfdf5; border-color: #a7f3d0; }
      .onboarding-num { width: 32px; height: 32px; border-radius: 50%; background: var(--gray-900); color: #fbbf24; font-weight: 700; display: grid; place-items: center; }
      .onboarding-step.done .onboarding-num { background: var(--success); color: #fff; }
      .onboarding-step strong { display: block; font-size: 15px; }
      .onboarding-step p { color: var(--gray-500); font-size: 13px; margin: 2px 0 0; }
      .onboarding-go { color: var(--primary); font-weight: 600; text-decoration: none; white-space: nowrap; }
      .plan-banner { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 10px 14px; border-radius: var(--border-radius); font-size: 14px; margin-bottom: 16px; border: 1px solid; }
      .plan-banner a { font-weight: 600; text-decoration: none; white-space: nowrap; }
      .plan-banner-info { background: #eff6ff; border-color: #bfdbfe; color: #1e40af; }
      .plan-banner-info a { color: #1e40af; }
      .plan-banner-warn { background: #fffbeb; border-color: #fde68a; color: #92400e; }
      .plan-banner-warn a { color: #92400e; }
      .dashboard-container {
        min-height: 100vh;
        background: var(--gray-50);
      }

      .dashboard-header {
        background: white;
        border-bottom: 1px solid var(--gray-200);
        padding: var(--spacing-md) var(--spacing-lg);
      }

      .header-content {
        max-width: 1280px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .logo {
        font-size: 24px;
        font-weight: 700;
        color: var(--danger);
        margin: 0;
      }

      .nav {
        display: flex;
        gap: var(--spacing-md);
      }

      .dashboard-main {
        max-width: 1280px;
        margin: 0 auto;
        padding: var(--spacing-lg);
      }

      .title-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-lg);
      }

      .title-section {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
      }

      .title-bar h2 {
        font-size: 28px;
        font-weight: 600;
        color: var(--gray-900);
        margin: 0;
      }

      .view-all-link {
        color: var(--primary);
        text-decoration: none;
        font-size: 16px;
        font-weight: 500;
      }

      .view-all-link:hover {
        text-decoration: underline;
      }

      .btn-primary {
        padding: 12px 24px;
        background: var(--primary);
        color: white;
        border: none;
        border-radius: var(--border-radius);
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }

      .btn-primary:hover {
        background: var(--primary-dark);
      }

      .btn-small {
        padding: 8px 16px;
        font-size: 14px;
      }

      .btn-secondary {
        padding: 10px 20px;
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-secondary:hover {
        background: var(--gray-50);
      }

      .loading {
        text-align: center;
        padding: 40px;
        color: var(--gray-500);
      }

      .error-message {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        margin-bottom: var(--spacing-md);
      }

      .empty-state {
        text-align: center;
        padding: 60px 20px;
        background: white;
        border-radius: var(--border-radius);
        border: 2px dashed var(--gray-300);
      }

      .empty-icon {
        font-size: 64px;
        margin-bottom: var(--spacing-md);
      }

      .empty-state h3 {
        font-size: 20px;
        color: var(--gray-900);
        margin-bottom: var(--spacing-sm);
      }

      .empty-state p {
        color: var(--gray-500);
      }

      @media (max-width: 640px) {
        .dashboard-header { padding: var(--spacing-sm) var(--spacing-md); }
        .header-content { flex-wrap: wrap; gap: var(--spacing-sm); }
        .logo { font-size: 20px; }
        .nav { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; gap: var(--spacing-sm); padding-bottom: 4px; scrollbar-width: none; }
        .nav::-webkit-scrollbar { display: none; }
        .nav > * { flex: 0 0 auto; }
        .dashboard-main { padding: var(--spacing-md); }
        .title-bar { flex-wrap: wrap; gap: var(--spacing-sm); }
        .title-bar h2 { font-size: 22px; }
        .title-section { flex-wrap: wrap; }
      }

      .attention { background: #fffbeb; border: 1px solid #fde68a; border-radius: var(--border-radius); padding: 16px 18px; margin-bottom: var(--spacing-lg); }
      .attention-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
      .attention-header h3 { font-size: 16px; color: #92400e; margin: 0; }
      .attention-link { color: #92400e; font-size: 14px; font-weight: 600; text-decoration: none; }
      .attention-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 8px 0; border-top: 1px solid #fde68a; }
      .attention-msg { background: none; border: none; padding: 0; text-align: left; font-size: 14px; color: #7c2d12; cursor: pointer; text-decoration: underline; font-family: inherit; }
      .attention-row.sev-critical .attention-msg { color: #991b1b; font-weight: 600; }
      .attention-dismiss { background: none; border: 1px solid #fcd34d; color: #92400e; border-radius: 6px; padding: 4px 10px; font-size: 12px; cursor: pointer; }
      .attention-dismiss[disabled] { opacity: 0.5; cursor: not-allowed; }

      .extinguishers-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
        gap: var(--spacing-lg);
      }

      .extinguisher-card {
        background: white;
        border-radius: var(--border-radius);
        padding: var(--spacing-lg);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        transition: box-shadow 0.2s;
        position: relative;
      }

      .extinguisher-card:hover {
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .status-badge {
        position: absolute;
        top: var(--spacing-md);
        right: var(--spacing-md);
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        color: white;
      }

      .status-compliant {
        background: var(--success);
      }

      .status-warning {
        background: #f59e0b;
      }

      .triage {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: var(--spacing-md);
        margin-bottom: var(--spacing-lg);
      }

      .triage-card {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        border: 1px solid;
        background: white;
        cursor: pointer;
        text-align: left;
        font: inherit;
        transition: box-shadow 0.15s, transform 0.15s;
      }

      .triage-card:hover {
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
        transform: translateY(-1px);
      }

      .triage-staff { border-color: #bfdbfe; background: #eff6ff; }
      .triage-contractor { border-color: #fde68a; background: #fffbeb; }
      .triage-staff .triage-count { color: #1d4ed8; }
      .triage-contractor .triage-count { color: #b45309; }

      .triage-count {
        font-size: 30px;
        font-weight: 700;
        line-height: 1.1;
      }

      .triage-label {
        font-size: 15px;
        font-weight: 600;
        color: #111827;
      }

      .triage-hint {
        font-size: 13px;
        color: #4b5563;
      }

      .status-overdue {
        background: var(--danger);
      }

      .qr-code {
        font-size: 20px;
        font-weight: 600;
        color: var(--gray-900);
        margin-bottom: var(--spacing-sm);
      }

      .extinguisher-type {
        font-size: 16px;
        color: var(--gray-700);
        margin-bottom: var(--spacing-sm);
      }

      .location {
        font-size: 14px;
        color: var(--gray-500);
        margin-bottom: var(--spacing-sm);
      }

      .last-inspection {
        font-size: 14px;
        color: var(--gray-500);
        margin-bottom: var(--spacing-md);
      }

      .card-actions {
        margin-top: var(--spacing-md);
        padding-top: var(--spacing-md);
        border-top: 1px solid var(--gray-200);
      }
    `;
  }

  /**
   * Open add extinguisher flow - navigate to locations to select one
   */
  private async openAddExtinguisherFlow(): Promise<void> {
    // Extinguishers are added from a location so they carry a place from day one.
    router.navigate('/locations');
    Toast.info('Pick a location, then add the extinguisher there.');
  }
}

// Register custom element
customElements.define('dashboard-page', DashboardPage);
